import { ref } from 'vue'

/**
 * access_token 放在本模块的模块级 ref，并镜像到 `sessionStorage`（本地会话）：
 *
 * - 内存态供请求层随取随用；`sessionStorage` 镜像让整页跳转到 `/oauth2/authorize`
 *   再回跳、或刷新回调页时令牌不丢（内存会在 SPA 重载时被清空）。
 * - 用 sessionStorage 而非 localStorage：随标签页关闭即清，不跨标签页、不留痕。
 *
 * 令牌本体放在本模块而不是 Pinia：请求层需要读写令牌，Pinia store 又要调用请求层，
 * 若令牌挂在 store 上会形成循环依赖。`stores/user.ts` 只是把这里的状态以只读方式暴露给组件。
 */
const STORAGE_KEY = 'uc.session.token'

/** 令牌随附的上下文（登录 / 切系统时服务端一并下发）。 */
export interface TokenContext {
  clientId?: string | null
  systemUuid?: string | null
  accountUuid?: string | null
}

interface StoredToken {
  accessToken: string
  expiresAt: number
  permVersion: string | null
  clientId: string | null
  systemUuid: string | null
  accountUuid: string | null
}

function loadStored(): StoredToken | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    return JSON.parse(raw) as StoredToken
  } catch {
    // 解析失败 / 隐私模式不可用：退回纯内存，不影响登录
    return null
  }
}

const stored = loadStored()

const accessToken = ref<string | null>(stored?.accessToken ?? null)
const expiresAt = ref<number>(stored?.expiresAt ?? 0)
const permVersion = ref<string | null>(stored?.permVersion ?? null)
/**
 * 令牌里带回来的当前系统标识（`client_id`，不是数字主键）。
 *
 * 它随令牌走而不是随 localStorage 走：换账号后旧的系统标识若还留在本地，
 * 菜单接口会按"上一个账号能看到的系统"去查，轻则 403，重则串到别的系统。
 */
const clientId = ref<string | null>(stored?.clientId ?? null)
/** 令牌里的系统对外编号：菜单 / 权限请求的 systemUuid 缺省取它。 */
const systemUuid = ref<string | null>(stored?.systemUuid ?? null)
/** 令牌里的账号对外编号：一个令牌只对应一个账号、一家公司。 */
const accountUuid = ref<string | null>(stored?.accountUuid ?? null)

/** 时钟漂移容忍：提前 30s 视为过期 */
const SKEW_MS = 30_000

export const tokenState = { accessToken, expiresAt, permVersion, clientId, systemUuid, accountUuid }

/** 把内存态令牌镜像到 sessionStorage，保证整页跳转 / SPA 重载 / 刷新回调页后仍在。 */
function persist(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      accessToken: accessToken.value,
      expiresAt: expiresAt.value,
      permVersion: permVersion.value,
      clientId: clientId.value,
      systemUuid: systemUuid.value,
      accountUuid: accountUuid.value,
    }))
  } catch {
    // sessionStorage 不可用：退回纯内存，不影响登录
  }
}

export function setAccessToken(token: string, expiresIn: number, version?: string | null,
  context?: TokenContext): void {
  accessToken.value = token
  expiresAt.value = Date.now() + Math.max(expiresIn - 5, 1) * 1000
  if (version !== undefined) {
    permVersion.value = version
  }
  if (context?.clientId !== undefined) {
    clientId.value = context.clientId
  }
  if (context?.systemUuid !== undefined) {
    systemUuid.value = context.systemUuid
  }
  if (context?.accountUuid !== undefined) {
    accountUuid.value = context.accountUuid
  }
  persist()
}

/**
 * 从 access_token 自身还原几个随令牌下发的上下文字段。
 *
 * 授权码链路（{@code /oauth2/token}）的响应只有标准的 {@code access_token / expires_in}，
 * 而菜单与权限要知道"这次登录落在哪个系统"；令牌是自包含的，直接读它比再发一轮请求更可靠。
 */
export function readTokenClaims(payload: string): {
  permVersion: string | null
  clientId: string | null
  systemUuid: string | null
  companyUuid: string | null
  accountUuid: string | null
} {
  const empty = { permVersion: null, clientId: null, systemUuid: null, companyUuid: null, accountUuid: null }
  try {
    const body = payload.split('.')[1]
    if (!body) {
      return empty
    }
    const claims: Record<string, unknown> = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')))
    const text = (value: unknown): string | null =>
      value === undefined || value === null || value === '' ? null : String(value)
    return {
      permVersion: text(claims['permVersion']),
      clientId: text(claims['client_id']),
      // system_uuid 决定菜单、权限与业务数据按哪个系统展开：令牌是唯一事实来源
      systemUuid: text(claims['system_uuid']),
      companyUuid: text(claims['company_uuid']),
      accountUuid: text(claims['account_uuid']),
    }
  } catch {
    // 令牌格式异常不该让登录流程中断：退化为"没有额外上下文"，最多是首帧菜单晚一点
    return empty
  }
}

export function clearAccessToken(): void {
  accessToken.value = null
  expiresAt.value = 0
  clientId.value = null
  systemUuid.value = null
  accountUuid.value = null
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // 忽略：存储不可用时内存态已清
  }
}

export function isAccessTokenUsable(): boolean {
  return accessToken.value !== null && Date.now() < expiresAt.value - SKEW_MS
}
